'use client';

import {
  BottomSheetHandleArea,
  BottomSheetTitle,
  BottomSheetTitleBox,
  BottomSheetTitleContainer,
  Dim,
  IcDelete,
  IconButton,
  Sheet,
  SheetContent,
  SheetDimMotion,
  SheetMotion,
  SheetPortal,
  SheetTrigger,
  Typography,
} from '@musinsa/mds';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import type React from 'react';
import { useState } from 'react';
import { usePreventBodyScroll } from '../../common/hooks/usePreventBodyScroll';

interface CuratorShopInfoBottomSheetProps {
  children: React.ReactNode;
}

export const CuratorShopInfoBottomSheet = ({ children }: CuratorShopInfoBottomSheetProps) => {
  const [isOpen, setIsOpen] = useState(false);

  // 바텀시트 열렸을 때 뒤쪽 스크롤 방지
  usePreventBodyScroll({ open: isOpen });

  return (
    <Sheet direction="bottom" defaultOpen={false} open={isOpen}>
      <SheetTrigger asChild onClick={() => setIsOpen(true)}>
        {children}
      </SheetTrigger>
      <SheetPortal>
        <SheetContent onPointerDownOutside={() => setIsOpen(false)}>
          <SheetMotion direction="bottom">
            <BottomSheetTitle>
              <BottomSheetHandleArea />
              <BottomSheetTitleContainer>
                <BottomSheetTitleBox className="py-3">
                  <Typography size="18" weight="medium">
                    큐레이션 샵 실적 안내
                  </Typography>
                </BottomSheetTitleBox>
                <IconButton size="size28x28" onClick={() => setIsOpen(false)}>
                  <IcDelete />
                </IconButton>
              </BottomSheetTitleContainer>
            </BottomSheetTitle>
            <CuratorShopInfoBottomSheetContent />
          </SheetMotion>
        </SheetContent>
        <SheetDimMotion>
          <Dim />
        </SheetDimMotion>
      </SheetPortal>
    </Sheet>
  );
};

const MUSINSA_GUIDE_CONTENTS = [
  `'큐레이션 샵'과 '쿠폰 적용 상품 페이지'를 통해 발생된 거래 건을 기준으로 한 실적입니다.`,
  `방문 : 큐레이션 샵과 쿠폰 적용 상품 페이지의 방문을 합산한 총 '방문 건 수' 입니다. 외부 제휴 링크를 통한 직접 진입만 방문으로 인정하며, 링크 진입 후 내부 페이지 간 이동은 방문 수에 포함되지 않습니다.`,
  `결제 건 : 큐레이션 샵과 쿠폰 적용 상품 페이지에서 상품을 주문하고 결제 완료 한 '총 결제 건 수' 입니다.`,
  `결제 금액 : 큐레이션 샵과 쿠폰 적용 상품 페이지에서 상품을 주문하고 결제 완료한 '총 결제 금액' 입니다. (배송비 제외)`,
  `예상 수익 : 예상 수익은 결제 건을 토대로 한 큐레이터님의 예상 수익을 의미합니다. (확정 수익 집계와 금액이 상이할 수 있습니다.)`,
  `확정 수익 : 활동월(N월)을 기준으로 익월 20일까지 구매 확정(취소/환불 반영)된 거래 건을 토대로 한 확정 수익입니다.`,
];

const DEFAULT_GUIDE_CONTENTS = [
  `'큐레이션 샵'을 통해 발생한 거래 건을 기준으로 한 실적입니다.`,
  `방문 : 큐레이션 샵에 방문한 '총 방문 건 수'입니다.`,
  `결제건 : 큐레이션 샵에서 상품을 주문하고 결제 완료한 '총 결제 건 수' 입니다.`,
  `결제 금액 : 큐레이션 샵에서 상품을 주문하고 결제 완료한 '총 결제 금액' 입니다. (배송비 제외)`,
  `예상 수익 : 예상 수익은 결제 건을 토대로 한 큐레이터님의 예상 수익입니다. (확정 수익 집계 구간과 상이하여 금액이 상이할 수 있습니다.)`,
  `확정 수익 : 활동월(N월)을 기준으로 익월 20일까지 구매 확정(취소/환불 반영)된 거래 건을 토대로 확정 수익입니다.`,
];

const CuratorShopInfoBottomSheetContent = () => {
  const { service } = useDeviceInfo();
  // NOTE: 쿠폰/쿠폰 적용 상품 페이지는 무신사에만 존재해, 실적 집계 범위·방문 정의가 29CM와 달라 가이드 문구를 분기한다.
  const contents = service === SERVICE.MUSINSA ? MUSINSA_GUIDE_CONTENTS : DEFAULT_GUIDE_CONTENTS;

  return (
    <div className="flex flex-col px-4 bg-white safe-area-bottom">
      <div className="flex flex-col pb-[12px]!">
        <ul>
          {contents.map((text) => (
            <li key={text} className="flex gap-[4px] py-1">
              <Typography colorScheme="gray-600" variant="body13pxReg">
                •
              </Typography>
              <Typography colorScheme="gray-600" variant="body13pxReg">
                {text}
              </Typography>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
